import { IconButton, ListItem, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, FileText, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteSpaceFileMutation,
  listDataSourcesOptions,
  listSpaceKnowledgeOptions,
  listSpaceKnowledgeQueryKey,
} from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";
import { UploadSpaceFilesButton } from "../UploadSpaceFilesButton";
import { SpaceItemsList } from "./SpaceItemsList";
import { useSettingsSpace } from "./useSpaceSettings";

function SpaceFiles({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.knowledge" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<{ uuid: string; filename: string } | null>(null);
  const path = { space_uuid: spaceUuid };
  const files = useQuery({ ...listSpaceKnowledgeOptions({ path }), select: (r) => r.data });
  const refresh = () => queryClient.invalidateQueries({ queryKey: listSpaceKnowledgeQueryKey({ path }) });
  const remove = useMutation({
    ...deleteSpaceFileMutation(),
    onSuccess: () => toast.success(t("fileDeleted")),
    onError: () => toast.error(t("deleteFailed")),
    onSettled: () => {
      setConfirming(null);
      return refresh();
    },
  });

  return (
    <section aria-label={t("files")} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <Text as="h2" variant="h3">
            {t("files")}
          </Text>
          <Text variant="caption" tone="tertiary">
            {t("filesHint")}
          </Text>
        </div>
        <UploadSpaceFilesButton spaceUuid={spaceUuid} />
      </div>
      <QuerySection
        query={files}
        isEmpty={(list) => list.length === 0}
        empty={{ icon: <FileText size={18} />, hint: t("filesEmpty") }}
      >
        {(list) => (
          <ul>
            {list.map((file, index) => (
              <li key={file.uuid}>
                <ListItem
                  divider={index > 0}
                  leading={<FileText size={15} className="text-content-tertiary" />}
                  description={formatRelativeTime(file.createdAt)}
                  trailing={
                    <IconButton
                      icon={<Trash2 />}
                      aria-label={t("deleteFile", { name: file.filename })}
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirming({ uuid: file.uuid, filename: file.filename })}
                    />
                  }
                >
                  {file.filename}
                </ListItem>
              </li>
            ))}
          </ul>
        )}
      </QuerySection>
      <ConfirmDialog
        open={confirming !== null}
        title={t("deleteConfirmTitle", { name: confirming?.filename ?? "" })}
        body={t("deleteConfirmBody")}
        confirmLabel={t("deleteConfirm")}
        loading={remove.isPending}
        onConfirm={() => confirming && remove.mutate({ path: { ...path, file_uuid: confirming.uuid } })}
        onClose={() => setConfirming(null)}
      />
    </section>
  );
}

export function KnowledgeSection() {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.knowledge" });
  const space = useSettingsSpace();
  const sources = useQuery({
    ...listDataSourcesOptions({ query: { page_size: 100 } }),
    select: (r) => r.data.map((d) => ({ uuid: d.uuid, name: d.name, description: d.description })),
  });
  return (
    <Page title={t("title")}>
      <SpaceFiles spaceUuid={space.uuid} />
      <SpaceItemsList
        spaceUuid={space.uuid}
        type="data_collection"
        title={t("sources")}
        addLabel={t("addSource")}
        addTitle={t("addSourceTitle")}
        emptyHint={t("sourcesEmpty")}
        icon={<Database size={18} />}
        openPath={(uuid) => `/build/data-sources/${uuid}`}
        options={sources}
      />
    </Page>
  );
}
